import { useRef } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import fetch from 'cross-fetch';

import { questionApi } from '../api';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { hideModal } from '../redux/modules/modal';
import { modifyArrayItemAt } from '../utils/arrayUtil';
import { queryClient } from '../utils/queryClientUtil';

import companyKeys from './models/company/keys';
import recordKeys from './models/record/keys';
import { camelCaseApiResponse, deleteInfiniteListsItem, modifyInfiniteListsItem } from './util';

export const useDeleteQuestion = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();
  const questionCategoryBaseKey = companyKeys.companyQuestionCategoryBase(companyId);

  return useMutation(
    ({ questionId }) => camelCaseApiResponse(() => questionApi.deleteQuestion(questionId)),
    {
      onMutate: () => {
        dispatch(createToast({
          title: 'Deleting Question...',
          status: 'info',
          duration: 10000
        }));
      },
      onSuccess: () => {
        dispatch(createSuccessToast({
          title: 'Question Deleted',
          duration: 2000
        }));

        queryClient.invalidateQueries(questionCategoryBaseKey);
      },
      onError: () => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your question was not deleted successfully.',
          duration: 2000
        }));
      }
    }
  );
};

export const useEditQuestionCategory = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();
  const questionCategoryBaseKey = companyKeys.companyQuestionCategoryBase(companyId);

  return useMutation(
    ({ questionCategoryId, name }) => camelCaseApiResponse(() => questionApi.editQuestionCategory(questionCategoryId, name)),
    {
      onMutate: () => {
        dispatch(createToast({
          title: 'Editing Question Category...',
          status: 'info',
          duration: 10000
        }));
      },
      onSuccess: () => {
        dispatch(createSuccessToast({
          title: 'Category Updated',
          duration: 2000
        }));

        queryClient.invalidateQueries(questionCategoryBaseKey);

        dispatch(hideModal());
      },
      onError: () => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your category was not edited successfully.',
          duration: 2000
        }));
      }
    }
  );
};

export const useDeleteQuestionCategory = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();
  const questionCategoryBaseKey = companyKeys.companyQuestionCategoryBase(companyId);

  return useMutation(
    ({ questionCategoryId }) => camelCaseApiResponse(() => questionApi.deleteQuestionCategory(questionCategoryId)),
    {
      onMutate: () => {
        dispatch(createToast({
          title: 'Deleting Question Category...',
          status: 'info',
          duration: 10000
        }));
      },
      onSuccess: () => {
        dispatch(createSuccessToast({
          title: 'Category Deleted',
          duration: 2000
        }));

        queryClient.invalidateQueries(questionCategoryBaseKey);
      },
      onError: () => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your category was not deleted successfully.',
          duration: 2000
        }));
      }
    }
  );
};

export const useCreateQuestionFavorite = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({
      categoryId,
      questionId,
      searchTerm
    }) => camelCaseApiResponse(() => questionApi.createQuestionFavorite(questionId)),
    {
      onMutate: async ({
        categoryId,
        questionId,
        searchTerm
      }) => {
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        await queryClient.cancelQueries(questionCategoryKey);

        const previousQuestionCategories = queryClient.getQueryData(questionCategoryKey);

        modifyInfiniteListsItem(
          [questionCategoryKey],
          'id',
          categoryId,
          category => {
            const updatedQuestions = modifyArrayItemAt(
              category.questions,
              'id',
              questionId,
              q => ({ ...q, favorite: 1 })
            );

            return {
              ...category,
              questions: updatedQuestions
            };
          }
        );

        // Fallback if mutation fails
        return { previousQuestionCategories };
      },
      onError: (error, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.setQueryData(questionCategoryKey, context.previousQuestionCategories);

        dispatch(createErrorToast({ title: 'Oops! Something went wrong...', description: 'Your question was not favorited successfully.', error }));
      },
      onSuccess: (data, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.invalidateQueries(questionCategoryKey);
      }
    }
  );
};

export const useRemoveQuestionFavorite = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({
      categoryId,
      questionId,
      searchTerm
    }) => camelCaseApiResponse(() => questionApi.removeQuestionFavorite(questionId)),
    {
      onMutate: async ({
        categoryId,
        questionId,
        searchTerm
      }) => {
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        await queryClient.cancelQueries(questionCategoryKey);

        const previousQuestionCategories = queryClient.getQueryData(questionCategoryKey);

        modifyInfiniteListsItem(
          [questionCategoryKey],
          'id',
          categoryId,
          category => {
            const updatedQuestions = modifyArrayItemAt(
              category.questions,
              'id',
              questionId,
              q => ({ ...q, favorite: 0 })
            );

            return {
              ...category,
              questions: updatedQuestions
            };
          }
        );

        // Fallback if mutation fails
        return { previousQuestionCategories };
      },
      onError: (error, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.setQueryData(questionCategoryKey, context.previousQuestionCategories);

        dispatch(createErrorToast({ title: 'Oops! Something went wrong...', description: 'Your question was not favorited successfully.', error }));
      },
      onSuccess: (data, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.invalidateQueries(questionCategoryKey);
      }
    }
  );
};

export const useCreateQuestionCategoryFavorite = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({ categoryId, searchTerm }) => camelCaseApiResponse(() => questionApi.createQuestionCategoryFavorite(categoryId)),
    {
      onMutate: async ({ categoryId, searchTerm }) => {
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });

        await queryClient.cancelQueries(questionCategoryKey);

        const previousQuestionCategories = queryClient.getQueryData(questionCategoryKey);

        modifyInfiniteListsItem(
          [questionCategoryKey],
          'id',
          categoryId,
          q => ({ ...q, favorite: 1 })
        );

        // Fallback if mutation fails
        return { previousQuestionCategories };
      },
      onError: (error, variables, context) => {
        dispatch(createErrorToast({ title: 'Oops! Something went wrong...', description: 'Your question category was not favorited successfully.', error }));

        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.setQueryData(questionCategoryKey, context.previousQuestionCategories);
      },
      onSuccess: (data, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.invalidateQueries(questionCategoryKey);
      }
    }
  );
};

export const useRemoveQuestionCategoryFavorite = ({ companyId, options = {} }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({ categoryId, searchTerm }) => camelCaseApiResponse(() => questionApi.removeQuestionCategoryFavorite(categoryId)),
    {
      onMutate: async ({ categoryId, searchTerm }) => {
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });

        await queryClient.cancelQueries(questionCategoryKey);

        const previousQuestionCategories = queryClient.getQueryData(questionCategoryKey);

        modifyInfiniteListsItem(
          [questionCategoryKey],
          'id',
          categoryId,
          q => ({ ...q, favorite: 0 })
        );

        // Fallback if mutation fails
        return { previousQuestionCategories };
      },
      onError: (error, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });

        dispatch(createErrorToast({ title: 'Oops! Something went wrong...', description: 'Your question category was not favorited successfully.', error }));

        queryClient.setQueryData(questionCategoryKey, context.previousQuestionCategories);
      },
      onSuccess: (data, variables, context) => {
        const { searchTerm } = variables;
        const questionCategoryKey = companyKeys.companyQuestionCategories(companyId, { searchTerm });
        queryClient.invalidateQueries(questionCategoryKey);
      }
    }
  );
};

export const useMarkQuestionAsRead = ({ recordId, options = {} }) => {
  const dispatch = useDispatch();
  const questionRequirementsKey = recordKeys.recordQuestionRequirements(recordId);

  return useMutation(
    ({ questionAnswerId }) => camelCaseApiResponse(() => questionApi.markQuestionAnswerAsRead(recordId, questionAnswerId)),
    {
      onMutate: async ({ questionAnswerId }) => {
        queryClient.cancelQueries(questionRequirementsKey);

        const previousQuestionRequirements = queryClient.getQueryData(questionRequirementsKey);

        const updatedQuestionRequirements = previousQuestionRequirements.reduce((acc, qR) => {
          const newAnswers = modifyArrayItemAt(
            qR.answers,
            'id',
            questionAnswerId,
            qa => ({ ...qa, viewedAt: !qa.viewedAt ? new Date().toISOString() : null })
          );

          const newQr = { ...qR, answers: newAnswers };

          return [...acc, newQr];
        }, []);

        queryClient.setQueryData(questionRequirementsKey, updatedQuestionRequirements);

        return { previousQuestionRequirements };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(questionRequirementsKey, context.previousQuestionRequirements);

        dispatch(createErrorToast({ title: 'Oops! Something went wrong...', description: 'Your question was not marked as read successfully.', error }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(questionRequirementsKey);
      },
      ...options
    }
  );
};

export const useEditQuestion = ({
  companyId,
  blueprint,
  options = {}
}) => {
  const dispatch = useDispatch();
  const companyQuestionCategoriesKeys = companyKeys.companyQuestionCategoryBase(companyId);

  return useMutation(
    ({
      questionId,
      questionCategoryId,
      name,
      question,
      redFlag,
      retroactive
    }) => camelCaseApiResponse(() => questionApi.editQuestion(questionId, questionCategoryId, name, question, redFlag, retroactive)),
    {
      onSuccess: async (data, variables) => {
        await queryClient.invalidateQueries(companyQuestionCategoriesKeys);

        if (variables.retroactive) {
          dispatch(blueprint.updateQuestion(variables.questionId, {
            id: data.id,
            questionCategoryId: data.questionCategoryId,
            name: data.name,
            question: data.question,
            redFlag: data.redFlag
          }));
        }

        dispatch(createSuccessToast({ title: 'Question updated successfully' }));
      },
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your question was not updated successfully.',
          error
        }));
      },
      ...options
    }
  );
};
